<template>
  <details-header :loading="loading" dense hide-avatar>
    <template v-slot:headerLine1>
      <span class="secondarytext--text">Feature Management</span>
    </template>
    <v-row>
      <v-col cols="12">
        <ad-header-card
          title="Let's setup your features 🏗️"
          subtitle="Select the features you'd like to have available for your organization"
        >
          <template v-slot:helpText>
            {{ settings.featureIds.length }} of 4 features activated
          </template>
        </ad-header-card>
      </v-col>
      <v-col cols="12">
        <v-card>
          <template v-for="(item, i) in featureItems">
            <feature-list-item
              :key="i"
              :title="item.title"
              :subtitle="item.subtitle"
              :icon="item.icon"
              :image="item.image"
              :url="
                item.disabled
                  ? '/billing'
                  : item.url
                  ? `/features/${item.url}/settings`
                  : null
              "
            >
              <template v-slot:titleExtension>
                <v-badge
                  inline
                  dot
                  :color="
                    settings.featureIds.includes(item.id)
                      ? 'success'
                      : 'lighttext'
                  "
                  class="subtitle-2 ml-2"
                >
                  <span
                    :class="{
                      'success--text': settings.featureIds.includes(item.id),
                      'lighttext--text': !settings.featureIds.includes(item.id)
                    }"
                  >
                    {{
                      settings.featureIds.includes(item.id)
                        ? "Enabled"
                        : "Disabled"
                    }}
                  </span>
                </v-badge>
              </template>
              <template v-slot:action>
                <v-btn text color="performance" v-if="item.disabled">
                  Upgrade
                </v-btn>
              </template>
              <template v-slot:helpText v-if="item.help">
                {{ item.help }}
              </template>
            </feature-list-item>
            <v-divider
              :key="i + 'index'"
              v-if="i < featureItems.length - 1"
            ></v-divider>
          </template>
        </v-card>
      </v-col>
    </v-row>
  </details-header>
</template>

<script>
import { mapGetters } from "vuex";
import DetailsHeader from "@/components/common/DetailsHeader";
import AdHeaderCard from "@/components/common/AdHeaderCard";
import FeatureListItem from "@/components/common/FeatureListItem";
export default {
  name: "FeatureManagement",
  components: {
    DetailsHeader,
    AdHeaderCard,
    FeatureListItem
  },
  data: () => ({
    loading: false
  }),
  computed: {
    featureItems() {
      let isLite = this.settings.planType != this.$enums.companyPlanTypes.SETUP;
      let isEngagement =
        this.settings.planType != this.$enums.companyPlanTypes.SETUP &&
        this.settings.planType != this.$enums.companyPlanTypes.LITE;
      let isPerformance =
        this.settings.planType == this.$enums.companyPlanTypes.ENGAGEMENT;
      let list = [
        {
          title: "Culture Tracking",
          subtitle:
            "Collect continuous feedback from your employees & get a better view into your company culture",
          help: !this.settings.isPulseActive
            ? "☝ We recommend starting here!"
            : "",
          image: this.$images.PULSE_ICON,
          id: this.$enums.features.PULSE,
          url: "tracking",
          disabled: !isLite
        },
        {
          title: "Create Internal Survey's",
          subtitle:
            "Whether you're looking for lunch ideas or employee feedback our survey builder makes it easy.",
          icon: "list",
          id: this.$enums.features.SURVEYS,
          url: "surveys",
          disabled: !isEngagement
        },
        {
          title: "Employee Recognition",
          subtitle:
            "Make it easy for anyone in your organization to recognize another employee's performance",
          icon: "thumb_up",
          id: this.$enums.features.RECOGNITION,
          url: "recognition",
          disabled: !isEngagement
        }
      ];

      if (this.settings.isReviewActive) {
        list.push({
          title: "360 Feedback",
          subtitle:
            "Easily collect continuous performance metrics on the employees across your organization.",
          icon: "360",
          id: this.$enums.features.REVIEW,
          url: "reviews",
          disabled: !isEngagement
        });
      }

      if (this.settings.isCheckInActive) {
        list.push({
          title: "Meeting Spaces",
          subtitle:
            "Ensure your team has an organized space to collaborate, manage goals & stay aligned.",
          icon: "question_answer",
          id: this.$enums.features.CHECK_INS,
          disabled: !isPerformance,
          url: "meetings"
        });
      }

      if (this.settings.isGoalsActive) {
        list.push({
          title: "Goal Tracking",
          subtitle:
            "Keep your team focused, set expectations and measure the progress along the way.",
          icon: "terrain",
          id: this.$enums.features.GOALS,
          disabled: !isPerformance,
          url: "goals"
        });
      }

      list.push({
        title: "Training Resources",
        subtitle:
          "Use our library of hand picked training resources from around the web & easily share them with your team",
        icon: "local_library",
        id: this.$enums.features.RESOURCE,
        disabled: !isEngagement,
        url: "training"
      });

      if (this.settings.isAnnualReviewActive) {
        list.push({
          title: "Annual Reviews",
          subtitle:
            "Simplify the review process by removing the bottlenecks & improving your view into each employee.",
          icon: "rate_review",
          id: this.$enums.features.ANNUAL_REVIEWS,
          disabled: !isPerformance,
          url: "annual-reviews"
        });
      }

      return list;
    },
    ...mapGetters({
      settings: "profile/settings"
    })
  },
  created() {
    this.loading = true;
    Promise.all([this.$store.dispatch("profile/getSettings")])
      .catch(error => {
        this.logError(error);
        this.$root.$alert(
          "Error Occurred",
          "An error occured while loading your settings."
        );
      })
      .finally(() => {
        this.loading = false;
      });
  }
};
</script>
